import React from "react";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Subheader from "@components/Subheader/Subheader";
import PageLayout from "@layouts/PageLayout";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

/**
 * DFT-0027 (Slice E1): Figma p13/p21 shows the Ongoing queue and the manual
 * Waiting List as two tabs of a single "Bay Hire" Operations page, not as the
 * two separate pages that cross-linked via buttons.
 *
 * Megaplan (2026-08-05, Operations 3-tab restructure): a 3rd tab, "History",
 * holds resolved/terminal requests (APPROVED/REJECTED/CANCELLED) - these used
 * to be lumped into the Ongoing tab (item 6's widening), mixing resolved
 * requests in with genuinely active ones. See
 * useBayHireOperationsQueue.tsx's STATUSES_BY_VIEW for the full 3-way
 * partition of all 7 statuses this introduces - every request now lives in
 * exactly one of the 3 tabs.
 *
 * Router-driven tabs, mirroring
 * features/vessel-call/vessel-call-status/Operations/OperationsContainer.tsx:
 * the active tab is derived from the URL (so all 3 tabs stay deep-linkable
 * and back/forward work) and onChange navigates rather than holding local
 * state.
 *
 * This container owns the page chrome (PageLayout + Subheader) that all queue
 * children used to render themselves - the URLs and paths.bayHire.* values are
 * unchanged for the Ongoing/Waiting-list tabs.
 */
const operationsTabs = [
  {
    labelKey: "bay-hire.operations-tabs.ongoing",
    path: paths.bayHire.operationsQueue,
    testId: "bay-hire-operations-tab-ongoing",
    subtitleKey: "bay-hire.operations-subtitle",
  },
  {
    labelKey: "bay-hire.operations-tabs.waiting-list",
    path: paths.bayHire.waitingList,
    testId: "bay-hire-operations-tab-waiting-list",
    subtitleKey: "bay-hire.operations-tabs.waiting-list-subtitle",
  },
  {
    labelKey: "bay-hire.operations-tabs.history",
    path: paths.bayHire.history,
    testId: "bay-hire-operations-tab-history",
    subtitleKey: "bay-hire.operations-tabs.history-subtitle",
  },
] as const;

const BayHireOperationsTabs: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  // Matched most-specific-first: the Ongoing path ("bay-hire/operations") is a
  // prefix of both the Waiting List and History ones, so a plain findIndex
  // would always resolve to the Ongoing tab on /bay-hire/operations/
  // waiting-list or /bay-hire/operations/history.
  const matchedIndex = [...operationsTabs]
    .map((tab, index) => ({ tab, index }))
    .reverse()
    .find(({ tab }) => location.pathname.includes(`/${tab.path}`))?.index;
  const currentTab = matchedIndex ?? 0;

  return (
    <PageLayout dir={dir}>
      <Subheader
        testId="bay-hire-operations-subheader"
        subtitle={t(operationsTabs[currentTab].subtitleKey)}
        title={t("bay-hire.title")}
      />
      <div className="px-24">
        <Tabs
          value={currentTab}
          onChange={(_, index: number) => navigate(`/${operationsTabs[index].path}`)}
          aria-label={t("bay-hire.operations-tabs.label")}
          data-testid="bay-hire-operations-tabs"
          sx={{
            "borderBottom": "1px solid var(--stroke-secondary-stroke, #E6EDF2)",
            "minHeight": 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "1rem",
              minHeight: 40,
              minWidth: "auto",
              px: 2,
            },
          }}
        >
          {operationsTabs.map((tab) => (
            <Tab key={tab.path} label={t(tab.labelKey)} data-testid={tab.testId} />
          ))}
        </Tabs>
      </div>

      <Outlet />
    </PageLayout>
  );
};

export default BayHireOperationsTabs;
